import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';

/**
 * navItem "Gruppen" of the ProvisionAgent OBC (04-frontend.md §7), widget alias
 * `cp.agent.groups.table`.
 *
 * Shows the PERIOD HISTORY of the agent -> provision type (+ group) assignment, i.e.
 * `cp.ProvisionAgentGroup` (R-02). An agent can be in several provision types in parallel
 * (U-06), but always only ONE period at a time per provision type; `endDate` is ALWAYS set by
 * the system (auto-close, last row 9999-12-31) and is therefore pure display here.
 *
 * Sorting: newest period first, so the currently valid assignment sits on top.
 * QL shape following the repo example `bm-billing-analytics.component.ts` (entity + left joins +
 * `resolve_fkey_texts`).
 *
 * REFRESH (04-frontend.md §4), following the pattern of the balance pane
 * {@link CpProvisionAgentBalanceComponent}: subscription to `MvsObjectService.objectChangeEvent` for
 * `cp.ProvisionAgentGroup` AND to `JbSpringBatchIndicatorService.status$`. The reason for the
 * second subscription: the calculation run auto-closes running periods (auto-close of
 * `endDate`, R-02) — without this subscription the history would keep showing, after the run,
 * the old, open period. Reloaded on the running -> not-running transition, in place via
 * `MvsWidgetComponent.refreshWidget()`, so sorting and scroll position are preserved.
 */
@Component({
    selector: 'cp-provision-agent-groups',
    templateUrl: './cp-provision-agent-groups.component.html',
    styleUrls: ['./cp-provision-agent-groups.component.scss'],
    standalone: false
})
export class CpProvisionAgentGroupsComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionAgentId: number;

    groupsWidget: WidgetData;

    /** Is a cp job currently running? */
    jobRunning: boolean;

    /**
     * The mounted widget instance — only for the in-place reload. `refreshWidget()` reloads,
     * WITHOUT touching the binding; a freshly re-assigned `groupsWidget` would remount the table
     * and lose sorting and scroll position (same rationale as in
     * `cp-provision-agents.component.ts`).
     */
    @ViewChild('groupsWidgetRef') groupsWidgetComponent: MvsWidgetComponent;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;
    protected objectChangeSubscription: Subscription;

    constructor(
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /** Reloads as soon as a group assignment was written anywhere in the module. */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionAgentGroup') {
                this.reloadInPlace();
            }
        });
    }

    /** Subscription to the job indicator; reload on the running -> not-running transition (04 §4). */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.reloadInPlace();
            }
        });
    }

    /** Reloads the table in place; builds one only if none exists yet. */
    protected reloadInPlace(): void {
        if (this.groupsWidgetComponent) {
            this.groupsWidgetComponent.refreshWidget();
            return;
        }
        this.refreshComponent();
    }

    refreshComponent(): void {

        if (!this.provisionAgentId) {
            this.groupsWidget = null;
            this.initialized = true;
            return;
        }

        const qlQuery: QlQueryDto = {
            name: 'cp.agent.groups',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgentGroup',
                as: 'e',
                joins: [
                    {type: 'entity', name: 'provisionType', as: 'pt', joinType: 'left'},
                    {type: 'entity', name: 'provisionTypeGroup', as: 'ptg', joinType: 'left'}
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionAgentId', FilterCriteria.cOperatorEqual, this.provisionAgentId)
            ],
            sortings: [new Sorting('e.startDate', false)]
        };

        /**
         * AI: Period history of an agent's assignment to provision types and groups.
         *     Source: QL view on cp.ProvisionAgentGroup, join provision type and group.
         *     Caseworkers see here which provision type applied to the agent from when.
         */
        this.groupsWidget = WidgetFactory.createWidgetTableQl(
            'cp.agent.groups.table',
            'Zuordnung zu Provisionstypen',
            qlQuery,
            WidgetDataParam.create('paging', 25),
            WidgetDataParam.create('size', 'S')
        );

        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
